/**
 * Consultant Store Activation
 *
 * Multi-step flow: verify a consultant (jafraId + phone) against the
 * existing AWS backend and then activate her digital store (email, postal
 * code, password). All markup/copy lives in
 * sections/consultant-store-activation.liquid; this file only owns behavior.
 *
 * The AWS host is never hardcoded here: it reuses window.HOST_AWS_URL, the
 * same global the rest of the theme uses (see layout/theme.liquid, custom.js,
 * coverage-modal.js, cart-share.liquid), which already resolves Sandbox vs
 * Production via settings.aws_server_url_development / _production.
 *
 * Backend contract (confirmed against the Lambda handlers):
 *   POST /Prod/Verify   body: { jafraId, phone }
 *                       200 -> { exists: true }
 *                       404 -> HttpError CUSTOMER_NOT_FOUND
 *   POST /Prod/Activate body: { jafraId, email, postalCode, password, confirmPassword }
 *                       200 -> { status: 'activo', passwordUpdated }
 *                       404 -> HttpError CUSTOMER_NOT_FOUND (jafraId/email don't match)
 *                       password/confirmPassword are validated server-side
 *                       (zod: min 1 char, must match) — there is no server-side
 *                       complexity rule, so the stronger checklist below (8-20
 *                       chars, upper/lower/number-or-symbol) is a client-side
 *                       UX choice mirrored from the existing password rules in
 *                       assets/custom.js, not a backend requirement.
 *   postalCode has no server-side format rule beyond "non-empty", so it is
 *   only required here, not restricted to a fixed length.
 */
(function () {
  "use strict";

  var STEPS = {
    VERIFY_FORM: "verify-form",
    VERIFY_LOADING: "verify-loading",
    VERIFY_ERROR: "verify-error",
    ACTIVATE_FORM: "activate-form",
    ACTIVATE_LOADING: "activate-loading",
    ACTIVATE_ERROR: "activate-error",
    SUCCESS: "success",
  };

  var STEP_META = {};
  STEP_META[STEPS.VERIFY_FORM] = { panel: "verify-form", dots: ["active", "pending", "pending"] };
  STEP_META[STEPS.VERIFY_LOADING] = { panel: "verify-form", dots: ["active", "pending", "pending"] };
  STEP_META[STEPS.VERIFY_ERROR] = { panel: "verify-form", dots: ["error", "pending", "pending"] };
  STEP_META[STEPS.ACTIVATE_FORM] = { panel: "activate-form", dots: ["done", "active", "pending"] };
  STEP_META[STEPS.ACTIVATE_LOADING] = { panel: "activate-form", dots: ["done", "active", "pending"] };
  STEP_META[STEPS.ACTIVATE_ERROR] = { panel: "activate-error", dots: ["done", "error", "pending"] };
  STEP_META[STEPS.SUCCESS] = { panel: "success", dots: ["done", "done", "done"] };

  var REQUEST_TIMEOUT_MS = 20000;
  var VERIFY_ENDPOINT_PATH = "ttd/verify";
  var ACTIVATE_ENDPOINT_PATH = "ttd/activation";

  function onlyDigits(value) {
    return (value || "").replace(/\D/g, "");
  }

  function stripSpaces(value) {
    return (value || "").replace(/\s/g, "");
  }

  function sanitizeFieldOnInput(input, sanitize) {
    if (!input) return;
    input.addEventListener("input", function () {
      var start = input.selectionStart;
      var value = input.value;
      var sanitized = sanitize(value);
      if (sanitized === value) return;
      var newPos = sanitize(value.slice(0, start)).length;
      input.value = sanitized;
      input.setSelectionRange(newPos, newPos);
    });
  }

  function isValidPhone(value) {
    return /^\d{10}$/.test(onlyDigits(value));
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function getPasswordChecks(password) {
    return {
      length: password.length >= 8 && password.length <= 20,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      symbol: /[\d!@#$%^&*?.,]/.test(password),
    };
  }

  function isPasswordValid(password) {
    var checks = getPasswordChecks(password);
    return checks.length && checks.upper && checks.lower && checks.symbol;
  }

  /**
   * Thin fetch wrapper: normalizes network errors, timeouts and invalid JSON
   * into a single { ok, status, data, networkError, timeout, parseError }
   * shape so callers never need to touch try/catch directly.
   */
  function requestJSON(url, options) {
    var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    var timeoutId = null;
    var fetchOptions = options || {};

    if (controller) {
      fetchOptions = Object.assign({}, options, { signal: controller.signal });
      timeoutId = setTimeout(function () {
        controller.abort();
      }, REQUEST_TIMEOUT_MS);
    }

    return fetch(url, fetchOptions)
      .then(function (res) {
        if (timeoutId) clearTimeout(timeoutId);
        return res.text().then(function (text) {
          var data = null;
          var parseError = false;
          if (text) {
            try {
              data = JSON.parse(text);
            } catch (e) {
              parseError = true;
            }
          }
          return { ok: res.ok && !parseError, status: res.status, data: data, parseError: parseError };
        });
      })
      .catch(function (err) {
        if (timeoutId) clearTimeout(timeoutId);
        var isAbort = err && err.name === "AbortError";
        return { ok: false, status: 0, data: null, networkError: true, timeout: isAbort };
      });
  }

  function ConsultantStoreActivation(root) {
    this.root = root;
    this.strings = this.readStrings();

    this.isSubmitting = false;
    this.state = null;
    this.currentPanelKey = null;
    this.verifyData = null; // { membership, phone } captured in step 1
    this.pendingActivateValues = null; // { email, postalCode, password, confirmPassword } for retry

    this.panels = this.mapByDataset("[data-ca-panel]", "caPanel");
    this.overlays = this.mapByDataset("[data-ca-overlay]", "caOverlay");
    this.banners = this.mapByDataset("[data-ca-banner]", "caBanner");
    this.fields = this.mapByDataset("[data-ca-field]", "caField");
    this.fieldErrors = this.mapByDataset("[data-ca-field-error]", "caFieldError");
    this.requirements = this.mapByDataset("[data-ca-requirement]", "caRequirement");
    this.forms = this.mapByDataset("[data-ca-form]", "caForm");
    this.dots = {};
    this.root.querySelectorAll("[data-ca-dot]").forEach(
      function (dot) {
        this.dots[dot.dataset.caDot] = dot;
      }.bind(this)
    );

    this.bindEvents();
    this.setState(STEPS.VERIFY_FORM, { skipFocus: true });
  }

  ConsultantStoreActivation.prototype.readStrings = function () {
    var el = this.root.querySelector("script[data-ca-strings]");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error("[consultant-activation] No se pudieron leer los textos configurados.", e);
      return {};
    }
  };

  ConsultantStoreActivation.prototype.mapByDataset = function (selector, datasetKey) {
    var map = {};
    this.root.querySelectorAll(selector).forEach(function (el) {
      map[el.dataset[datasetKey]] = el;
    });
    return map;
  };

  ConsultantStoreActivation.prototype.bindEvents = function () {
    var self = this;

    if (this.forms.verify) {
      this.forms.verify.addEventListener("submit", function (e) {
        e.preventDefault();
        self.handleVerifySubmit();
      });
    }

    if (this.forms.activate) {
      this.forms.activate.addEventListener("submit", function (e) {
        e.preventDefault();
        self.handleActivateSubmit();
      });
    }

    if (this.fields.password) {
      this.fields.password.addEventListener("input", function () {
        self.updatePasswordRequirements(self.fields.password.value);
      });
    }

    sanitizeFieldOnInput(this.fields.membership, onlyDigits);
    sanitizeFieldOnInput(this.fields.phone, onlyDigits);
    sanitizeFieldOnInput(this.fields.email, stripSpaces);

    this.root.querySelectorAll("[data-ca-back]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.handleBack(btn.dataset.caBack);
      });
    });

    var retryBtn = this.root.querySelector('[data-ca-retry="activate"]');
    if (retryBtn) {
      retryBtn.addEventListener("click", function () {
        self.retryActivate();
      });
    }
  };

  // ---------------------------------------------------------------------
  // State machine
  // ---------------------------------------------------------------------

  ConsultantStoreActivation.prototype.setState = function (step, opts) {
    var meta = STEP_META[step];
    if (!meta) return;
    opts = opts || {};

    var panelChanged = this.currentPanelKey !== meta.panel;
    this.state = step;
    this.currentPanelKey = meta.panel;

    Object.keys(this.panels).forEach(
      function (key) {
        this.panels[key].hidden = key !== meta.panel;
      }.bind(this)
    );

    if (this.overlays.verify) this.overlays.verify.hidden = step !== STEPS.VERIFY_LOADING;
    if (this.overlays.activate) this.overlays.activate.hidden = step !== STEPS.ACTIVATE_LOADING;
    if (this.banners.verify) this.banners.verify.hidden = step !== STEPS.VERIFY_ERROR;

    meta.dots.forEach(
      function (dotState, index) {
        var dot = this.dots[String(index + 1)];
        if (!dot) return;
        if (dotState === "pending") {
          dot.removeAttribute("data-ca-state");
        } else {
          dot.setAttribute("data-ca-state", dotState);
        }
      }.bind(this)
    );

    if (panelChanged && !opts.skipFocus) {
      this.focusPanelHeading(meta.panel);
    }
  };

  ConsultantStoreActivation.prototype.focusPanelHeading = function (panelKey) {
    var panel = this.panels[panelKey];
    if (!panel) return;
    var heading = panel.querySelector("h2");
    if (!heading) return;
    if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
    heading.focus({ preventScroll: false });
  };

  // ---------------------------------------------------------------------
  // Field error helpers
  // ---------------------------------------------------------------------

  ConsultantStoreActivation.prototype.setFieldError = function (name, message) {
    var input = this.fields[name];
    var errorEl = this.fieldErrors[name];
    if (!input || !errorEl) return;
    if (message) {
      input.setAttribute("aria-invalid", "true");
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      input.removeAttribute("aria-invalid");
      errorEl.textContent = "";
      errorEl.hidden = true;
    }
  };

  ConsultantStoreActivation.prototype.clearFieldErrors = function (names) {
    names.forEach(
      function (name) {
        this.setFieldError(name, "");
      }.bind(this)
    );
  };

  ConsultantStoreActivation.prototype.focusFirstInvalid = function (names) {
    for (var i = 0; i < names.length; i++) {
      var input = this.fields[names[i]];
      if (input && input.getAttribute("aria-invalid") === "true") {
        input.focus();
        return;
      }
    }
  };

  ConsultantStoreActivation.prototype.setFormDisabled = function (form, disabled) {
    if (!form) return;
    form.querySelectorAll("input, button").forEach(function (el) {
      el.disabled = disabled;
    });
    var panel = form.closest("[data-ca-panel]");
    if (panel) panel.setAttribute("aria-busy", disabled ? "true" : "false");
  };

  ConsultantStoreActivation.prototype.updatePasswordRequirements = function (password) {
    var checks = getPasswordChecks(password || "");
    Object.keys(checks).forEach(
      function (key) {
        var el = this.requirements[key];
        if (!el) return;
        el.setAttribute("data-ca-met", checks[key] ? "true" : "false");
      }.bind(this)
    );
  };

  // ---------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------

  ConsultantStoreActivation.prototype.validateVerifyForm = function () {
    var membership = (this.fields.membership.value || "").trim();
    var phone = (this.fields.phone.value || "").trim();
    var valid = true;

    this.clearFieldErrors(["membership", "phone"]);

    if (!membership) {
      this.setFieldError("membership", this.strings.verifyRequiredError);
      valid = false;
    }

    if (!phone) {
      this.setFieldError("phone", this.strings.verifyRequiredError);
      valid = false;
    } else if (!isValidPhone(phone)) {
      this.setFieldError("phone", this.strings.phoneInvalidError);
      valid = false;
    }

    return valid ? { membership: membership, phone: onlyDigits(phone) } : null;
  };

  ConsultantStoreActivation.prototype.validateActivateForm = function () {
    var email = (this.fields.email.value || "").trim();
    var postalCode = (this.fields.postalCode.value || "").trim();
    var password = this.fields.password.value || "";
    var confirmPassword = this.fields.confirmPassword.value || "";
    var valid = true;

    this.clearFieldErrors(["email", "postalCode", "password", "confirmPassword"]);

    if (!email) {
      this.setFieldError("email", this.strings.emailRequiredError);
      valid = false;
    } else if (!isValidEmail(email)) {
      this.setFieldError("email", this.strings.emailInvalidError);
      valid = false;
    }

    if (!postalCode) {
      this.setFieldError("postalCode", this.strings.postalCodeRequiredError);
      valid = false;
    }

    if (!password || !isPasswordValid(password)) {
      this.setFieldError("password", this.strings.passwordRequiredError);
      valid = false;
    }

    if (!confirmPassword) {
      this.setFieldError("confirmPassword", this.strings.confirmPasswordRequiredError);
      valid = false;
    } else if (confirmPassword !== password) {
      this.setFieldError("confirmPassword", this.strings.confirmPasswordMismatchError);
      valid = false;
    }

    return valid
      ? { email: email, postalCode: postalCode, password: password, confirmPassword: confirmPassword }
      : null;
  };

  // ---------------------------------------------------------------------
  // Verify step
  // ---------------------------------------------------------------------

  ConsultantStoreActivation.prototype.handleVerifySubmit = function () {
    if (this.isSubmitting) return;
    var values = this.validateVerifyForm();
    if (!values) {
      this.focusFirstInvalid(["membership", "phone"]);
      return;
    }

    this.verifyData = values;
    this.runVerify(values);
  };

  ConsultantStoreActivation.prototype.runVerify = function (values) {
    var self = this;
    var host = window.HOST_AWS_URL;

    if (!host) {
      console.error(
        "[consultant-activation] window.HOST_AWS_URL no está definido. Revisa settings.aws_server_url_production / aws_server_url_development en el tema."
      );
      this.setState(STEPS.VERIFY_ERROR);
      return;
    }

    this.isSubmitting = true;
    this.setState(STEPS.VERIFY_LOADING);
    this.setFormDisabled(this.forms.verify, true);

    var url = "https://" + host + "/Prod/" + VERIFY_ENDPOINT_PATH;

    requestJSON(url, {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ jafraId: values.membership, phone: values.phone }),
    }).then(function (result) {
      self.isSubmitting = false;
      self.setFormDisabled(self.forms.verify, false);

      if (self.isVerifySuccessful(result)) {
        self.setState(STEPS.ACTIVATE_FORM);
      } else {
        self.logRequestError("verify", result);
        self.setState(STEPS.VERIFY_ERROR);
      }
    });
  };

  /**
   * POST /Prod/Verify returns 200 { exists: true } when the consultant is
   * found, and a 404 HttpError (CUSTOMER_NOT_FOUND) otherwise — so a
   * non-2xx status (already reflected in result.ok) is always a failure.
   */
  ConsultantStoreActivation.prototype.isVerifySuccessful = function (result) {
    if (!result.ok || result.data == null || typeof result.data !== "object") return false;
    return result.data.exists === true;
  };

  // ---------------------------------------------------------------------
  // Activate step
  // ---------------------------------------------------------------------

  ConsultantStoreActivation.prototype.handleActivateSubmit = function () {
    if (this.isSubmitting) return;
    var values = this.validateActivateForm();
    if (!values) {
      this.focusFirstInvalid(["email", "postalCode", "password", "confirmPassword"]);
      return;
    }

    this.pendingActivateValues = values;
    this.runActivate(values);
  };

  ConsultantStoreActivation.prototype.retryActivate = function () {
    if (this.isSubmitting) return;
    if (!this.pendingActivateValues) {
      this.setState(STEPS.ACTIVATE_FORM);
      return;
    }
    this.runActivate(this.pendingActivateValues);
  };

  ConsultantStoreActivation.prototype.runActivate = function (values) {
    var self = this;
    var host = window.HOST_AWS_URL;

    if (!host) {
      console.error(
        "[consultant-activation] window.HOST_AWS_URL no está definido. Revisa settings.aws_server_url_production / aws_server_url_development en el tema."
      );
      this.setState(STEPS.ACTIVATE_ERROR);
      return;
    }

    this.isSubmitting = true;
    this.setState(STEPS.ACTIVATE_LOADING);
    this.setFormDisabled(this.forms.activate, true);

    var url = "https://" + host + "/Prod/" + ACTIVATE_ENDPOINT_PATH;
    var payload = this.buildActivatePayload(values);

    requestJSON(url, {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    }).then(function (result) {
      self.isSubmitting = false;
      self.setFormDisabled(self.forms.activate, false);

      if (result.ok) {
        self.pendingActivateValues = null;
        self.clearPasswordFields();
        self.setState(STEPS.SUCCESS);
      } else {
        self.logRequestError("activate", result);
        self.setState(STEPS.ACTIVATE_ERROR);
      }
    });
  };

  /**
   * Matches the Activate Lambda's zod schema exactly: jafraId, email,
   * postalCode, password, confirmPassword. phone is not part of this
   * contract (the handler looks the customer up by jafraId + email), so it
   * is intentionally left out even though we collected it in step 1.
   */
  ConsultantStoreActivation.prototype.buildActivatePayload = function (values) {
    return {
      jafraId: this.verifyData.membership,
      email: values.email,
      postalCode: values.postalCode,
      password: values.password,
      confirmPassword: values.confirmPassword,
    };
  };

  ConsultantStoreActivation.prototype.clearPasswordFields = function () {
    if (this.fields.password) this.fields.password.value = "";
    if (this.fields.confirmPassword) this.fields.confirmPassword.value = "";
    this.updatePasswordRequirements("");
  };

  // ---------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------

  ConsultantStoreActivation.prototype.handleBack = function (target) {
    if (this.isSubmitting) return;

    if (target === "verify") {
      this.setState(STEPS.VERIFY_FORM);
      if (this.fields.membership) this.fields.membership.focus();
    } else if (target === "activate") {
      this.clearPasswordFields();
      this.setState(STEPS.ACTIVATE_FORM);
    }
  };

  // ---------------------------------------------------------------------
  // Logging (never logs password/confirmPassword/tokens or full payloads)
  // ---------------------------------------------------------------------

  ConsultantStoreActivation.prototype.logRequestError = function (context, result) {
    var status = result.status;
    var detail = {
      networkError: !!result.networkError,
      timeout: !!result.timeout,
      parseError: !!result.parseError,
    };

    if (result.timeout) {
      console.error("[consultant-activation] " + context + ": la petición superó el tiempo de espera.", detail);
    } else if (result.networkError) {
      console.error("[consultant-activation] " + context + ": error de red.", detail);
    } else if (result.parseError) {
      console.error("[consultant-activation] " + context + ": respuesta con JSON inválido.", detail);
    } else if (status === 400) {
      console.error("[consultant-activation] " + context + ": solicitud inválida (400).", detail);
    } else if (status === 401) {
      console.error("[consultant-activation] " + context + ": no autorizado (401).", detail);
    } else if (status === 403) {
      console.error("[consultant-activation] " + context + ": prohibido (403).", detail);
    } else if (status === 404) {
      console.error("[consultant-activation] " + context + ": recurso no encontrado (404).", detail);
    } else if (status === 409) {
      console.error("[consultant-activation] " + context + ": conflicto (409).", detail);
    } else if (status === 422) {
      console.error("[consultant-activation] " + context + ": entidad no procesable (422).", detail);
    } else if (status >= 500) {
      console.error("[consultant-activation] " + context + ": error del servidor (" + status + ").", detail);
    } else {
      console.error("[consultant-activation] " + context + ": fallo inesperado.", Object.assign({ status: status }, detail));
    }
  };

  // ---------------------------------------------------------------------
  // Init — idempotent, multi-instance safe, Theme Editor aware
  // ---------------------------------------------------------------------

  function initAll(scope) {
    var root = scope || document;
    var nodes = [];

    if (root.matches && root.matches("[data-consultant-activation]")) {
      nodes.push(root);
    }
    if (root.querySelectorAll) {
      root.querySelectorAll("[data-consultant-activation]").forEach(function (node) {
        nodes.push(node);
      });
    }

    nodes.forEach(function (node) {
      if (node.dataset.caInitialized === "true") return;
      node.dataset.caInitialized = "true";
      new ConsultantStoreActivation(node);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
